<template>
    <div v-if="loading" class="flex items-center justify-center w-full p-20">
        <progress-bar class="w-[50vw] lg:w-[25vw]" mode="indeterminate" style="height: 2px" />
    </div>

    <div v-else-if="tours">
        <div v-if="searchable" class="py-2">
            <p-select placeholder="Choose your destination..." fluid size="large" v-model="destination"
                @change="(e) => apply({ destination: e.value })" :options="uniqBy(destinations.filter((l) => l.slug !== 'default'), (l) => l.country_slug)" :option-label="(l) => l.region ?? l.country_name" :option-value="'slug'"
                :option-group-label="(option) => option.country_name" :option-group-children="(option) =>
                    destinations.filter(
                        (l) => l.country_slug == option.country_slug
                    )
                    ">
                <template #optiongroup="{ option }">
                    <div class="uppercase font-thin text-sm">
                        {{ option.country_name }}
                    </div>
                </template>
            </p-select>
        </div>

        <DataView v-if="['list', 'grid'].includes(layout)" :value="tours" :layout="layout" lazy data-key="id"
            :rows="pagination ? pagination.perPage : undefined"
            :total-records="pagination ? pagination.total : undefined" :current-page-report-template="paginate
                ? 'Showing {first} to {last} of {totalRecords}'
                : undefined
                " :first="pagination
                    ? pagination.perPage * pagination.currentPage -
                    (pagination.perPage + 1)
                    : undefined
                    " @update:first="0">
            <template #grid="{ items }">
                <div class="grid grid-cols-2 gap-2 md:gap-3 lg:grid-cols-4">
                    <div v-for="item, idx in items" class="my-3">
                        <tour-item :tour="item" :url="getRoute(item)" layout="grid" :loading="filtering"
                            :hide-cta="hideCta" :priority="props.priority && idx < 4" v-model:height="syncedHeight"
                            @click="registerProductClick(item, idx + 1)"
                            @impression="registerProductImpression(item, idx + 1)" />
                    </div>
                </div>
            </template>

            <template #list="{ items }">
                <div class="flex items-center justify-center">
                    <div class="flex flex-col w-full gap-2 py-10 md:w-3/4">
                        <tour-item v-for="item, idx in items" :tour="item" :url="getRoute(item)" layout="list"
                            :loading="filtering" :priority="props.priority && idx < 2"
                            @click="registerProductClick(item, idx + 1)"
                            @impression="registerProductImpression(item, idx + 1)" />
                    </div>
                </div>
            </template>

            <template v-if="pagination || !fixedlayout || sortable" #header>
                <div class="flex items-center justify-between">
                    <div class="text-sm font-thin">
                        <span v-if="pagination">
                            {{ `${pagination.count} of ${pagination.total}` }}
                        </span>
                    </div>

                    <div class="flex justify-end items-center flex-wrap gap-2">
                        <p-button v-if="!fixedlayout" @click="toggleLayout" size="small" :icon="layout == 'grid'
                            ? 'pi pi-list'
                            : 'pi pi-th-large'
                            " severity="secondary" :label="layout == 'grid' ? 'List' : 'Grid'" />
                        <p-select v-if="sortable" :options="order_by_options"
                            @change="(e) => apply({ order_by: e.value })" v-model="order_by" option-value="slug"
                            option-label="display_as" size="small" />
                    </div>
                </div>
            </template>

            <template v-if="(country  && !props.tours) || (region && !props.tours) || pagination" #footer>

                <div v-if="pagination" class="flex items-center justify-center w-full">
                    <div v-if="pagination.totalPages > pagination.currentPage"
                        class="flex flex-col items-center justify-center w-full md:w-1/2">
                        <div class="text-sm font-thin">
                            {{
                                `Showing ${pagination.count} of ${pagination.total}`
                            }}
                        </div>
                        <p-button fluid :label="`Show the next ${pagination.currentPage == pagination.totalPages - 1
                            ? pagination.total - pagination.count
                            : pagination.perPage
                            } tours`" @click="onPage" :loading="loadingContent" :disabled="loadingContent" />
                    </div>
                </div>
                <div v-else class="flex justify-center">
                    <div class="w-1/2 md:w-1/3">
                        <p-button :as="Link" :href="route('tours', {
                            ...(data ?? {}),
                            filter: true,
                            country,
                            region,
                        })
                            " fluid v-if="tours.length >= 8" label="See All" severity="contrast" size="small" />
                    </div>
                </div>
            </template>
        </DataView>

        <div v-else-if="layout == 'carousel'">
            <div class="flex items-center justify-between">
                <div class="text-sm font-thin">
                    <span v-if="pagination">
                        {{
                            `Showing ${pagination.count} of ${pagination.total}`
                        }}
                    </span>
                </div>
            </div>

            <carousel v-if="tours.length >= 3" circular :value="tours" :num-visible="3" :num-scroll="3"
                :responsive-options="[
                    {
                        breakpoint: '1024px',
                        numScroll: 3,
                        numVisible: 1,
                    },
                    {
                        breakpoint: '768px',
                        numScroll: 2,
                        numVisible: 1,
                    },
                    {
                        breakpoint: '640px',
                        numScroll: 1,
                        numVisible: 1,
                    },
                ]">
                <template #item="{ data: tour, index: idx }">
                    <div class="p-3">
                        <tour-item :tour="tour" :url="getRoute(tour)" layout="grid" :loading="filtering"
                            :hide-cta="hideCta" v-model:height="syncedHeight"
                            @click="registerProductClick(tour, idx + 1)"
                            @impression="registerProductImpression(tour, idx + 1)" />
                    </div>
                </template>
            </carousel>


            <ScrollSlider v-else>
                <div v-for="tour, idx in tours"
                    class="shrink-0 snap-start w-[calc(50%-0.375rem)] lg:w-[calc(25%-0.5625rem)]">
                    <tour-item :tour="tour" :url="getRoute(tour)" layout="grid" :loading="filtering"
                        :hide-cta="hideCta" :height="maxHeight"
                        @click="registerProductClick(tour, idx + 1)"
                        @impression="registerProductImpression(tour, idx + 1)" />
                </div>
            </ScrollSlider>
        </div>

        <div v-else-if="layout == 'scroll'">
            <div class="flex items-center justify-between">
                <div class="text-sm font-thin">
                    <span v-if="pagination">
                        {{
                            `Showing ${pagination.count} of ${pagination.total}`
                        }}
                    </span>
                </div>
            </div>
            <ScrollSlider>
                <div v-for="tour, idx in tours"
                    class="shrink-0 snap-start w-[calc(50%-0.375rem)] lg:w-[calc(25%-0.5625rem)]">
                    <tour-item :tour="tour" :url="getRoute(tour)" layout="grid" :loading="filtering"
                        :hide-cta="hideCta" v-model:height="maxHeight"
                        @click="registerProductClick(tour, idx + 1)"
                        @impression="registerProductImpression(tour, idx + 1)" />
                </div>
            </ScrollSlider>
        </div>

        <!-- Rail: one tour at a time with the full-width feature treatment
             (video/collage). Multiple tours become a carousel with grouped
             prev/next controls. Used for the related-tours sidebar on posts. -->
        <div v-else-if="layout == 'rail'">
            <related-tours-carousel :tours="tours" :route-for="getRoute"
                @view="(tour, pos) => registerProductClick(tour, pos)"
                @impression="(tour, pos) => registerProductImpression(tour, pos)" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { useApiProxy } from "@/Hook/useProxy";
import { Location, OrderByOption, Pagination, Tour } from "@/types";
import { computed, onMounted, ref, watch } from "vue";
import { Carousel } from "primevue";
import {
    ProgressBar,
    DataView,
    Button as PButton,
    Select as PSelect,
} from "primevue";
import TourItem from "@/Components/Tour/Item/DefaultItem.vue";
import RelatedToursCarousel from "@/Components/Tour/RelatedToursCarousel.vue";
import { Link, router, usePage } from "@inertiajs/vue3";
import uniqBy from "lodash/uniqBy";
import { useSession } from "@/Hook/useSession";
import ScrollSlider from "../ScrollSlider.vue";
import useAgency from "@/Hook/useAgency";
import useDataLayer from "@/Hook/useDataLayer";

const { country, region, data, fixedlayout, ...props } = defineProps<{
    data?: { [key: string]: any };
    country?: string;
    region?: string;
    layout?: "grid" | "list" | "carousel" | "scroll" | "rail";
    fixedlayout?: boolean;
    searchable?: boolean;
    filter?: boolean;
    tours?: Tour[];
    paginate?: boolean;
    sortable?: boolean;
    cache?: boolean;
    hideCta?: boolean;
    // When true, eager-load the first row's images so this strip can provide the
    // Largest Contentful Paint element. Set only on above-the-fold strips.
    priority?: boolean;
    // Server-resolved payload for the `[tours ...]` CMS shortcode, injected by
    // ShortcodeResolver so the fetch-mode listing renders during SSR.
    ssrData?: { tours?: Tour[]; pagination?: Pagination };
}>();

const order_by_options = ref<OrderByOption[]>([]);

const order_by = ref<string>();

const params = ref<Record<string, any>>({});

const pagination = ref<Pagination | undefined>(props.ssrData?.pagination);

// Seed from server-provided tours synchronously so the list renders during
// SSR. `tours` covers Explore/similar strips; `ssrData` covers the CMS
// shortcode. Only when neither is present does the component fetch on mount.
const tours = ref(props.tours ?? props.ssrData?.tours);

// Keep in sync when the server sends new tours (e.g. Inertia navigation
// re-uses this component with a fresh `tours` prop).
watch(() => props.tours, (val) => {
    if (val) tours.value = val;
});

const maxHeight = ref()
const syncedHeight = computed({
    get: () => (props.hideCta ? undefined : maxHeight.value),
    set: (v) => { if (!props.hideCta) maxHeight.value = v },
})

const session = useSession()


const reseller = useAgency()

const layout = ref(props.layout ?? session.get('tours_comp_layout', props.layout ?? "grid"));

const destination = ref();


const { registerProductImpression, registerProductClick } = useDataLayer()

const destinations: Location[] = usePage<any>().props
    .__destinations as Location[];

const { loading, request } = useApiProxy();

const { loading: loadingContent, request: paginationRequest } = useApiProxy(); 

const filtering = ref(false);



const getSearchUrl = function (data?: Record<string, any>) {
    setPageParams();
    if (params.value?.destination) {
        const __destination = destinations.find(d => d.slug == params.value?.destination);
        params.value = { ...params.value, country: __destination?.country_slug }
    }


    if (data) {
        params.value = { ...params.value, ...data };
    }

    return route("tours", params.value);
}

const getTours = function () {
    if (props.tours) {
        tours.value = props.tours;
        return;
    }

    // Server already resolved this shortcode's tours — reuse them instead of
    // re-fetching on mount (keeps hydration stable, no flash).
    if (props.ssrData?.tours) {
        tours.value = props.ssrData.tours;
        if (props.ssrData.pagination) pagination.value = props.ssrData.pagination;
        return;
    }


    request(
        getSearchUrl(),
        {
            headers: {
                "Content-Type": "application/json",
            },
        },
        props.cache ?? true
    ).then(function (_data: any) {
        tours.value = _data.tours ?? [];
        pagination.value = _data.pagination ?? undefined;
    });
};




const onPage = function () {
    const page = (pagination.value?.currentPage ?? 0) + 1;

    paginationRequest(
        getSearchUrl({
            page, json: true
        }),
        {
            method: "GET",
        }
    ).then(function (data: { tours: Tour[]; pagination?: Pagination; meta: any }) {
        tours.value = [...(tours.value ?? []), ...data.tours];

        if (data.pagination)
            pagination.value = {
                ...(pagination.value ?? data.pagination),
                currentPage: page,
                count: tours.value.length,
            };
    })
};






const getRoute = function (tour: Tour) {
    const params: Record<string, any> = { id: tour.id, tour_slug: tour.slug };

    const specific = usePage().props?.specific_tours;

    if (reseller.hasAgency.value) {
        params['agent_id'] = reseller.agent.value
        params['agency_id'] = reseller.agencyId.value
    }

    let country_slug = country;
    let region_slug = region;

    if (destination.value) {
        const location = destinations.find((l) => l.slug == destination.value);

        country_slug = location?.country_slug;
        region_slug = location?.region_slug;
    }

    if (country_slug && region_slug)
        return route(specific ? 'specific.show.tour.region' : "show.tour.region", {
            ...params,
            country: country_slug,
            region: region_slug,
        });

    if (country_slug)
        return route(specific ? 'specific.show.tour.country' : "show.tour.country", {
            ...params,
            country: country_slug,
        });

    return route(specific ? "specific.show.tour" : "show.tour", params);
};

const toggleLayout = function () {
    if (fixedlayout) return;
    if (layout.value == "grid") layout.value = "list";
    else layout.value = "grid";


    session.set('tours_comp_layout', layout.value);
};

const setPageParams = function () {
    const { destination: __destination, ..._params } =
        usePage<any>().props.__params?.tours ?? {};

    if (__destination) destination.value = __destination;

    const resellerParams: Record<string, any> = {};


    if (reseller.hasAgency.value) {
        resellerParams['agent_id'] = reseller.agent.value
        resellerParams['agency_id'] = reseller.agencyId.value
    }

    params.value = {
        ...(data ?? {}),
        json: true,
        paginate: props.paginate,
        filter: true,
        ...(__destination
            ? { destination: __destination }
            : { country: country ?? "none", region }),
        ..._params,
        ...resellerParams
    };
};

const apply = function (params?: { [key: string]: any }) {
    const _params = usePage<any>().props.__params?.tours ?? {};
    filtering.value = true;
    router.get(
        location.href,
        { tours: { ..._params, ...(params ?? {}) } },
        {
            preserveScroll: true,
            onFinish: () => {
                getTours();
                filtering.value = false;
            },
        }
    );
};

onMounted(function () {
    getTours();
    const params = usePage().props.__params;
    order_by_options.value = usePage().props.order_by_options;
    order_by.value =
        params?.tours?.order_by ??
        order_by_options.value.find((sort) => sort.is_default)?.slug;
});
</script>

<style scoped>
.p-dataview-header {
    border: none !important;
}
</style>
